import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { GlassCard, PageLoader } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Shield, Users, Bell, KeyRound, Check, Plus, Trash2 } from "lucide-react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [mfaSetup, setMfaSetup] = useState(null);
  const [code, setCode] = useState("");
  const [team, setTeam] = useState([]);
  const [channels, setChannels] = useState([]);
  const [invite, setInvite] = useState({ email: "", role: "read_only" });
  const [newCh, setNewCh] = useState({ type: "webhook", target: "" });
  const [ackEnc, setAckEnc] = useState(false);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data));
    api.get("/notification-channels").then((r) => setChannels(r.data));
  }, []);

  const startMfa = async () => { const { data } = await api.post("/auth/mfa/setup"); setMfaSetup(data); };
  const enableMfa = async () => {
    try { await api.post("/auth/mfa/enable", { totp: code }); toast.success("MFA enabled"); setMfaSetup(null); setCode(""); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const disableMfa = async () => { await api.post("/auth/mfa/disable"); toast.success("MFA disabled"); refresh(); };

  const addMember = async () => {
    try { await api.post("/team", invite); toast.success("Invited"); setInvite({ email: "", role: "read_only" }); api.get("/team").then((r) => setTeam(r.data)); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addChannel = async () => {
    try { await api.post("/notification-channels", { ...newCh, frequency: "immediate" }); setNewCh({ type: "webhook", target: "" }); api.get("/notification-channels").then((r) => setChannels(r.data)); toast.success("Channel added"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delChannel = async (id) => { await api.delete(`/notification-channels/${id}`); api.get("/notification-channels").then((r) => setChannels(r.data)); };

  if (!user) return <PageLoader />;

  return (
    <div>
      <h1 className="mb-6 font-display text-2xl font-bold text-slate-50">Settings</h1>
      <Tabs defaultValue="security">
        <TabsList className="bg-slate-900">
          <TabsTrigger value="security" data-testid="tab-security"><Shield className="mr-1.5 h-4 w-4" />Security</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team"><Users className="mr-1.5 h-4 w-4" />Team</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications"><Bell className="mr-1.5 h-4 w-4" />Notifications</TabsTrigger>
          <TabsTrigger value="encryption" data-testid="tab-encryption"><KeyRound className="mr-1.5 h-4 w-4" />Encryption</TabsTrigger>
        </TabsList>

        <TabsContent value="security" className="mt-5">
          <GlassCard className="max-w-lg p-6">
            <h2 className="font-display font-semibold text-slate-100">Two-factor authentication</h2>
            <p className="mt-1 text-sm text-slate-400">Strongly recommended. Protect your account with a TOTP authenticator app.</p>
            {user.mfa_enabled ? (
              <div className="mt-4"><span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-3 py-1 text-sm text-emerald-300"><Check className="h-4 w-4" /> MFA enabled</span>
                <button onClick={disableMfa} data-testid="disable-mfa" className="ml-3 text-sm text-rose-400 hover:underline">Disable</button></div>
            ) : mfaSetup ? (
              <div className="mt-4">
                <p className="text-sm text-slate-400">Add this secret to your authenticator app:</p>
                <div className="mt-2 break-all rounded-lg border border-slate-800 bg-[#06080D] p-3 font-mono text-sm text-cyan-300">{mfaSetup.secret}</div>
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" data-testid="mfa-code" className="mt-3 bg-slate-950 border-slate-700" />
                <button onClick={enableMfa} data-testid="enable-mfa-confirm" className="mt-3 rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#06080D]">Verify & enable</button>
              </div>
            ) : (
              <button onClick={startMfa} data-testid="setup-mfa" className="mt-4 rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#06080D]">Set up MFA</button>
            )}
          </GlassCard>
        </TabsContent>

        <TabsContent value="team" className="mt-5">
          <GlassCard className="p-6">
            <h2 className="mb-4 font-display font-semibold text-slate-100">Team members</h2>
            <div className="space-y-2">
              {team.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-[#06080D] p-3 text-sm">
                  <span className="text-slate-200">{m.email}</span>
                  <span className="rounded-full bg-slate-800 px-2.5 py-0.5 text-xs capitalize text-slate-300">{m.role.replace("_", " ")}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Input placeholder="teammate@company.com" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="invite-email" className="flex-1 bg-slate-950 border-slate-700" />
              <select value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} data-testid="invite-role" className="rounded-lg border border-slate-700 bg-slate-950 px-3 text-sm text-slate-200">
                <option value="administrator">Administrator</option><option value="backup_admin">Backup Administrator</option><option value="billing">Billing</option><option value="read_only">Read Only</option>
              </select>
              <button onClick={addMember} disabled={!invite.email} data-testid="invite-submit" className="inline-flex items-center gap-1 rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#06080D] disabled:opacity-50"><Plus className="h-4 w-4" /> Invite</button>
            </div>
          </GlassCard>
        </TabsContent>

        <TabsContent value="notifications" className="mt-5">
          <GlassCard className="p-6">
            <h2 className="mb-4 font-display font-semibold text-slate-100">Notification channels</h2>
            <div className="space-y-2">
              {channels.map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-[#06080D] p-3 text-sm">
                  <span><span className="rounded bg-slate-800 px-2 py-0.5 text-xs capitalize text-cyan-300">{c.type}</span> <span className="ml-2 text-slate-300">{c.target}</span></span>
                  <button onClick={() => delChannel(c.id)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <select value={newCh.type} onChange={(e) => setNewCh({ ...newCh, type: e.target.value })} data-testid="channel-type" className="rounded-lg border border-slate-700 bg-slate-950 px-3 text-sm text-slate-200">
                <option value="webhook">Webhook</option><option value="slack">Slack</option><option value="teams">Teams</option><option value="email">Email</option>
              </select>
              <Input placeholder="URL or email" value={newCh.target} onChange={(e) => setNewCh({ ...newCh, target: e.target.value })} data-testid="channel-target" className="flex-1 bg-slate-950 border-slate-700" />
              <button onClick={addChannel} disabled={!newCh.target} data-testid="channel-add" className="inline-flex items-center gap-1 rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#06080D] disabled:opacity-50"><Plus className="h-4 w-4" /> Add</button>
            </div>
            <p className="mt-3 text-xs text-slate-500">Alert categories: Backup Failed/Missed, Server Offline, Storage 80/90/Full, Verification Failed, GC Failed, Auth Failure, and more — each supports Immediate, Daily Digest or Disabled.</p>
          </GlassCard>
        </TabsContent>

        <TabsContent value="encryption" className="mt-5">
          <GlassCard className="max-w-2xl p-6">
            <h2 className="font-display font-semibold text-slate-100">Client-side encryption</h2>
            <p className="mt-2 text-sm text-slate-400">We strongly recommend enabling Proxmox client-side backup encryption. <b className="text-slate-200">Your encryption key belongs to you.</b> ZettaKeep does not automatically possess your private backup encryption key.</p>
            <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
              <p className="text-sm text-amber-200/90"><b>Without your encryption key, encrypted backups cannot be restored.</b> Store it somewhere safe and independent of your infrastructure.</p>
            </div>
            <label className="mt-4 flex items-center gap-3 text-sm text-slate-300">
              <Switch checked={ackEnc} onCheckedChange={(v) => { setAckEnc(v); if (v) toast.success("Acknowledgment recorded"); }} data-testid="ack-encryption" />
              I confirm I have safely stored my backup encryption key.
            </label>
          </GlassCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
